
import {fromEvent as observableFromEvent, Subscription, timer} from 'rxjs';
import {Directive, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {bufferTime, filter, map, share, skipWhile, switchMap, tap} from "rxjs/operators";
import {WINDOW_LISTENER_TOKEN, WindowEvent, WindowHostListenerService} from "../../window/window.host.listener.service";
import {SystemConfigurationValues} from "../../configuration/services/system.configuration.utils";

@UntilDestroy()
@Directive({
	selector: '[cpCardInput]',
})
export class CardInputDirective implements OnInit, OnDestroy{

	// tslint:disable-next-line:no-input-rename
	@Input('cpCardInput') cpCardInputRegex: string;
	@Input() cpCardInputGlobalEnabled: boolean = false;
	@Input() cpCardInputBufferTime: number; //In milliseconds
	@Input() cpCardInputMinInputLength: number;
	@Output() cpCardInputOnMatch: EventEmitter<string[]> = new EventEmitter<string[]>();

	private inputRegex: RegExp;
	private cardInputBufferTime: number;
	private cardInputMinLength: number;

	private elementSubscription: Subscription;
	private globalSubscription: Subscription;

	constructor(private el: ElementRef,
				private sysCfg: SystemConfigurationValues,
				@Inject(WINDOW_LISTENER_TOKEN) private windowHostListenerService: WindowHostListenerService) {
	}

	ngOnInit(): void {
		this.inputRegex = new RegExp(this.cpCardInputRegex);
		this.cardInputBufferTime = this.cpCardInputBufferTime || this.sysCfg.getNumber("CardInputDefaultBufferTime",2000) || 2000;
		this.cardInputMinLength = this.cpCardInputMinInputLength || this.sysCfg.getNumber("CardInputDefaultMinLength",15) || 15;

		this.subscribeToKeyDownEvents();
	}

	ngOnDestroy(): void {
		this.unsubscribeAllSubscriptions();
	}

	private subscribeToKeyDownEvents(): void {
		this.elementSubscription = this.subscribeToKeyDownEvent(this.el.nativeElement, (value) => this.checkInputRegex(value));
		if (this.cpCardInputGlobalEnabled) {
			this.globalSubscription = this.subscribeToGlobalKeyDownEvent();
		}
	}

	private unsubscribeAllSubscriptions(): void {
		if (this.elementSubscription) {
			this.elementSubscription.unsubscribe();
		}
		if (this.globalSubscription) {
			this.globalSubscription.unsubscribe();
		}
	}

	private resubscribeToKeyDownEvents(): void {
		this.unsubscribeAllSubscriptions();
		this.subscribeToKeyDownEvents();
	}

	private subscribeToGlobalKeyDownEvent(): Subscription {
		let keyDownObservable = this.windowHostListenerService.getObservable(WindowEvent.keydown);

		return keyDownObservable.pipe(untilDestroyed(this),
			filter((event: KeyboardEvent) => event.key.length === 1),
			map((event: KeyboardEvent) => event.key),
			bufferTime(this.cardInputBufferTime),
			map((charArray: Array<string>) => charArray.join('')),
			skipWhile((value) => value.length < this.cardInputMinLength))
			.subscribe((value) => {
				//Check only if no input DOM element is focused
				if (!document.activeElement || document.activeElement.tagName !== "INPUT" && document.activeElement.tagName !== "TEXTAREA") {
					this.checkInputRegex(value);
				}
			});
	}

	private subscribeToKeyDownEvent(target, valueHandler: Function): Subscription {
		const keyup$ = observableFromEvent(target, "keyup").pipe(share());

		// Alternative approach using switchMap to handle buffering manually
		let currentBuffer: string[] = [];
		let currentTriggerInfo: any = null;

		return keyup$.pipe(
			untilDestroyed(this),
			switchMap((event: KeyboardEvent) => {
				// Handle Enter key
				if (event.key === 'Enter') {
					const value = currentBuffer.join('');
					currentTriggerInfo = { type: 'enter', event };
					currentBuffer = []; // Reset buffer
					
					if (value.length >= this.cardInputMinLength) {
						return [{ value, triggerInfo: currentTriggerInfo }];
					}

					return [];
				}
				
				// Handle character keys
				if (event.key.length === 1) {
					currentBuffer.push(event.key);
					
					// Set up timer for buffer timeout
					return timer(this.cardInputBufferTime).pipe(
						map(() => {
							const value = currentBuffer.join('');
							currentTriggerInfo = { type: 'timeout' };
							currentBuffer = []; // Reset buffer
							
							return { value, triggerInfo: currentTriggerInfo };
						}),
						filter(({ value }) => value.length >= this.cardInputMinLength)
					);
				}
				
				return [];
			}),
			filter(result => result && result.value !== undefined) // Only proceed if we have a valid result
		).subscribe(({ value, triggerInfo }) => {
			let result = valueHandler(value);

			// Prevent default search on enter
			if (result && triggerInfo.type === 'enter') {
				triggerInfo.event.stopPropagation();
				triggerInfo.event.preventDefault();
			}
		});
	}

	private checkInputRegex(input: string) {
		if (!input || (input.length < this.cardInputMinLength)) { //Skip check for small, non card strings
			return false;
		}

		let regExResult: RegExpMatchArray = this.inputRegex[Symbol.match](input);

		if (regExResult && regExResult.length) {
			let matchResults = [];
			for (let i=1; i < regExResult.length; i++) {
				//return the first non-empty match group
				if (regExResult[i]) {
					matchResults.push(regExResult[i]);
				}
			}

			if (matchResults.length) {
				this.cpCardInputOnMatch.emit(matchResults);
				//Reset the current buffer with the previous patient card info
				this.resubscribeToKeyDownEvents();

				return true;
			}
		}

		return false;
	}
}
